export const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
export function formatDateUserFriendly(dateString: string): string {
  const [day, month, year] = dateString.split("-");
  const monthIndex = parseInt(month) - 1;
  const formattedYear = year.slice(-2);

  return `${parseInt(day)} ${months[monthIndex]} ${formattedYear}`;
}

// input - 2024-01-01 output - January 1
export function formatDateMD(dateString: string): string {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const [day, month, year] = dateString.split("-");
  const monthIndex = parseInt(month) - 1;

  return `${months[monthIndex]} ${parseInt(day)}`;
}
export function GetNumberOfDaysInMonth(month: string, year: number): number {
  const monthIndex: { [key: string]: number } = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };

  const monthLowerCase = month.toLowerCase().slice(0, 3);

  if (monthIndex[monthLowerCase] !== undefined) {
    // This will return the number of days in the given month
    return new Date(year, monthIndex[monthLowerCase] + 1, 0).getDate();
  } else {
    throw new Error(
      "Invalid month input. Please provide a valid month abbreviation (e.g., 'jan', 'feb')."
    );
  }
}

// Define a dictionary for quarters based on the month
const quarterMap: { [key: string]: string } = {
  jan: "Q4",
  feb: "Q4",
  mar: "Q4", // Q4 (Jan - Mar)
  apr: "Q1",
  may: "Q1",
  jun: "Q1", // Q1 (Apr - Jun)
  jul: "Q2",
  aug: "Q2",
  sep: "Q2", // Q2 (Jul - Sep)
  oct: "Q3",
  nov: "Q3",
  dec: "Q3", // Q3 (Oct - Dec)
};

// Function to get fiscal year based on month and year
export function getFiscalYear(month: string, year: number): string {
  const lowerCaseMonth = month.toLowerCase().slice(0, 3);

  // Check if the month belongs to Q4 (Jan-Mar)
  if (
    lowerCaseMonth === "jan" ||
    lowerCaseMonth === "feb" ||
    lowerCaseMonth === "mar"
  ) {
    return `${year - 1}-${year}`; // Fiscal year for Jan-Mar is the previous year to the current year
  } else {
    return `${year}-${year + 1}`; // Fiscal year for Apr-Dec is the current year to the next year
  }
}

// Function to get the quarter based only on the month and return it as "Q1", "Q2", etc.
export function getQuarter(month: string): string {
  const lowerCaseMonth = month.toLowerCase();
  return quarterMap[lowerCaseMonth] || "Invalid month"; // Return 'Invalid month' if the month is not valid
}

export function convertToString(date: Date): string {
  // Convert the date to a string in the format 'YYYY-MM-DD'
  return date.toISOString().split("T")[0];
}

export function getMonthFromDate(date: string): string {
  // Extract the month from the date string
  return months[parseInt(date.split("-")[1]) - 1].slice(0, 3);
}

export function getYearFromDate(date: string): string {
  // Extract the year from the date string
  return date.split("-")[0];
}

export function getDayFromDate(date: string): string {
  // Extract the day from the date string in yyyy-mm-dd format and remove leading zeros
  return parseInt(date.split("-")[2]).toString();
}

// Helper function to fill missing data based on dateIndex pattern
export function fillMissingDataByDateIndex<T extends { date: string; block: number; dateIndex: string }>(
  data: T[],
  startDate: string,
  endDate: string,
  discoms: string[] = [],
  createEmptyItem: (date: string, block: number, dateIndex: string, discom?: string) => T
): T[] {
  // Generate all dates in the range
  const allDates: string[] = [];
  const currentDate = new Date(startDate);
  const endDateObj = new Date(endDate);
  
  while (currentDate <= endDateObj) {
    allDates.push(currentDate.toISOString().split('T')[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Generate all possible combinations
  const allCombinations: Array<{ date: string; block: number; dateIndex: string; discom?: string }> = [];
  
  allDates.forEach((date, index) => {
    const dateIndex = `${index + 1} Day`;
    for (let block = 1; block <= 96; block++) {
      if (discoms.length > 0) {
        discoms.forEach(discom => {
          allCombinations.push({ date, block, dateIndex, discom });
        });
      } else {
        allCombinations.push({ date, block, dateIndex });
      }
    }
  });

  // Create a map of existing data
  const existingDataMap = new Map<string, T>();
  data.forEach(item => {
    const key = discoms.length > 0 
      ? `${item.date}-${item.block}-${(item as any).discom || ''}`
      : `${item.date}-${item.block}`;
    existingDataMap.set(key, item);
  });

  // Fill missing data
  const filledData: T[] = [];
  
  allCombinations.forEach(combo => {
    const key = discoms.length > 0 
      ? `${combo.date}-${combo.block}-${combo.discom || ''}`
      : `${combo.date}-${combo.block}`;
    
    const existingItem = existingDataMap.get(key);
    
    if (existingItem) {
      filledData.push(existingItem);
    } else {
      // Create empty item using the provided function
      const newItem = createEmptyItem(combo.date, combo.block, combo.dateIndex, combo.discom);
      filledData.push(newItem);
    }
  });

  return filledData;
}

export function fillMissingDateBlocks<T extends { date: string; block: number }>(
  data: T[],
  startDate: string,
  endDate: string,
  discoms: string[] = [],
  defaultValues: Partial<T> = {}
): T[] {
  // Generate all dates in the range
  const allDates: string[] = [];
  const currentDate = new Date(startDate);
  const endDateObj = new Date(endDate);
  
  while (currentDate <= endDateObj) {
    allDates.push(currentDate.toISOString().split('T')[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Generate all possible combinations of date, block, and discom
  const allCombinations: Array<{ date: string; block: number; discom?: string }> = [];
  
  allDates.forEach(date => {
    for (let block = 1; block <= 96; block++) {
      if (discoms.length > 0) {
        discoms.forEach(discom => {
          allCombinations.push({ date, block, discom });
        });
      } else {
        allCombinations.push({ date, block });
      }
    }
  });

  // Create a map of existing data for quick lookup
  const existingDataMap = new Map<string, T>();
  data.forEach(item => {
    const key = discoms.length > 0 
      ? `${item.date}-${item.block}-${(item as any).discom || ''}`
      : `${item.date}-${item.block}`;
    existingDataMap.set(key, item);
  });

  // Fill missing data with zeros
  const filledData: T[] = [];
  
  allCombinations.forEach(combo => {
    const key = discoms.length > 0 
      ? `${combo.date}-${combo.block}-${combo.discom || ''}`
      : `${combo.date}-${combo.block}`;
    
    const existingItem = existingDataMap.get(key);
    
    if (existingItem) {
      // Use existing data
      filledData.push(existingItem);
    } else {
      // Create new item with default/zero values
      const newItem = {
        ...defaultValues,
        date: combo.date,
        block: combo.block,
        ...(combo.discom && { discom: combo.discom }),
        // Set numeric fields to 0 if they exist in the type
        ...(typeof (defaultValues as any)?.drawal_mw === 'number' ? { drawal_mw: 0 } : {}),
        ...(typeof (defaultValues as any)?.schedule_mw === 'number' ? { schedule_mw: 0 } : {}),
        ...(typeof (defaultValues as any)?.load_shedding_mw === 'number' ? { load_shedding_mw: 0 } : {}),
        ...(typeof (defaultValues as any)?.total_mw === 'number' ? { total_mw: 0 } : {}),
        ...(typeof (defaultValues as any)?.drawal_mwh === 'number' ? { drawal_mwh: 0 } : {}),
        ...(typeof (defaultValues as any)?.schedule_mwh === 'number' ? { schedule_mwh: 0 } : {}),
        ...(typeof (defaultValues as any)?.load_shedding_mwh === 'number' ? { load_shedding_mwh: 0 } : {}),
        ...(typeof (defaultValues as any)?.total_mwh === 'number' ? { total_mwh: 0 } : {}),
        ...(typeof (defaultValues as any)?.drawal_lu === 'number' ? { drawal_lu: 0 } : {}),
        ...(typeof (defaultValues as any)?.schedule_lu === 'number' ? { schedule_lu: 0 } : {}),
        ...(typeof (defaultValues as any)?.load_shedding_lu === 'number' ? { load_shedding_lu: 0 } : {}),
        ...(typeof (defaultValues as any)?.total_lu === 'number' ? { total_lu: 0 } : {}),
        month: getMonthFromDate(combo.date),
        dates: `${startDate} to ${endDate}`,
        dateIndex: getDayFromDate(combo.date) + " Day",
      } as unknown as T;
      
      filledData.push(newItem);
    }
  });

  return filledData;
}


export function getMonthYear({
  month,
  year,
}: {
  month: number;
  year: number;
}): string {
  return `${(months[month] ?? "January").toString().slice(0, 3)}-${year
    .toString()
    .slice(2)}`;
}
export function formatDateRange(input: any) {
  // Extract the year and month from the first date
  const match = input.match(/(\d{4})-(\d{2})-\d{2}/);
  if (!match) return input;

  const year = match[1];
  const month = match[2];

  // Convert month number to abbreviated month name
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthName = monthNames[parseInt(month, 10) - 1];

  // Return formatted string
  return `${monthName} ${year.slice(2)}`;
}
